import { useState } from 'react';
import { useNotify } from 'ra-core';
import { lireLesChiffresDuneStructure } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';
import { useStructureCourante } from '../../fournisseurs/structure-courante';

type Chiffres = {
  tableauxAuPlafond: number;
  coursAvecUnTableauPlein: number;
  poidsMoyenDunTableauOctets: number;
  personnesAuPlafondDeVolume: number;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ÉCRAN DE CHIFFRES OUVERT À LA STRUCTURE — F-67, PREMIER DES DEUX.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-67 : « **DEUX ÉCRANS, ET DEUX SEULEMENT**, là où quatre domaines en ont rendu cinq.
 * **L'écran ouvert à la structure** porte les **cinq indicateurs d'exploitation du tableau** et
 * le **nombre de personnes au plafond de volume**, sur son périmètre. **Le compte qui voit tout
 * les lit sur le parc ET sur chaque structure.** »
 *
 * « **Tous ces chiffres sont calculés à l'affichage.** Aucun objet suivi, aucun compteur
 * entretenu. » Et « **un filtre de période, commun aux deux écrans** » : sans période, un
 * nombre d'événements ne veut rien dire.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **QUATRE CHIFFRES, ET DEUX ONT ÉTÉ RETIRÉS DU PÉRIMÈTRE LE 20260824 — D-080.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-67 en portait six. **Deux sont retirés**, sur le constat de D-075 : « dépôts refusés
 * faute de place » et « part des cours tenus sur tablette ». **Aucun des deux n'avait de
 * source au modèle** — la première n'a aucune table, la seconde a l'appareil d'une session
 * mais **rien qui relie un cours à la session qui l'a tenu**.
 *
 * **Ils sortaient vides, et c'est ce qui a été tranché.** Le porteur : « une cellule qui
 * décrit ce qui n'existe pas est une promesse qu'on ne tient pas ». Le précédent est Q399,
 * qui a retiré l'indicateur des documents fabriqués « tombé avec sa trace ».
 *
 * **CET ÉCRAN NE PORTE DONC PLUS D'AVERTISSEMENT D'INDISPONIBILITÉ**, et son absence est le
 * résultat : il n'y a plus de trou à expliquer. Le motif du retrait vit au contrat, sur le
 * schéma de réponse, et au journal — **pas sur l'écran**, où il aurait décrit une case qui
 * n'existe plus.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CET ÉCRAN NE PORTE PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Les six chiffres réservés**, §6.14 : « **les six chiffres réservés**, qu'il ne voit nulle
 * part » — champ interdit pour l'`orgadmin`. Ils vivent sur l'autre écran, et pour lui il n'a
 * pas d'écran du tout.
 *
 * **La présence en ligne**, F-67 : « **elle n'est pas un chiffre et ne vit pas ici** : elle se
 * lit **une personne à la fois**, sur la liste des comptes et sur la fiche. »
 *
 * **Aucune alerte, aucun seuil, aucun email** sur ces chiffres : « le seul email d'exploitation
 * du produit reste **celui qui prévient le compte qui voit tout quand le passage d'effacement
 * des scènes ne tourne plus**. »
 */
export function ChiffresDuneStructure() {
  const { structureId, selecteurAffiche } = useStructureCourante();
  const notifier = useNotify();
  const [chiffres, setChiffres] = useState<Chiffres | null>(null);
  const [periode, setPeriode] = useState<{ du: string; au: string }>({ du: '', au: '' });
  const [enCours, setEnCours] = useState(false);

  const lire = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    if (structureId === null) return;
    setEnCours(true);
    try {
      const reponse = await deballer(
        lireLesChiffresDuneStructure({
          path: { id: structureId },
          query: {
            ...(periode.du !== '' ? { du: new Date(periode.du).toISOString() } : {}),
            ...(periode.au !== '' ? { au: new Date(periode.au).toISOString() } : {}),
          },
        }),
      );
      setChiffres(reponse);
    } catch (erreur) {
      notifier(lireLErreur(erreur).message, { type: 'error' });
    } finally {
      setEnCours(false);
    }
  };

  if (structureId === null) {
    return (
      <main className="p-6">
        <Alert>
          <AlertTitle>Choisissez une structure.</AlertTitle>
          <AlertDescription>
            {selecteurAffiche
              ? 'Ces quatre chiffres portent sur une structure. Prenez-en une dans le sélecteur en ' +
                'tête de la surface — le compte qui voit tout les lit sur chaque structure.'
              : 'Aucun rattachement actif n’est associé à ce compte.'}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <main className="flex max-w-4xl flex-col gap-6 p-6">
      <h2 className="text-lg font-medium">Chiffres d’exploitation</h2>

      <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void lire(e)}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="du">Du</Label>
          <Input
            id="du"
            type="date"
            value={periode.du}
            onChange={(e) => setPeriode((p) => ({ ...p, du: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="au">Au</Label>
          <Input
            id="au"
            type="date"
            value={periode.au}
            onChange={(e) => setPeriode((p) => ({ ...p, au: e.target.value }))}
          />
        </div>
        <Button type="submit" disabled={enCours}>
          Calculer
        </Button>
        <span className="text-muted-foreground text-xs">
          Sans période : depuis toujours, jusqu’à maintenant. Tout est calculé à l’affichage —
          aucun compteur n’est entretenu.
        </span>
      </form>

      {chiffres !== null && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Indicateur
            titre="Tableaux au plafond"
            valeur={String(chiffres.tableauxAuPlafond)}
          />
          <Indicateur
            titre="Cours avec un tableau plein"
            valeur={String(chiffres.coursAvecUnTableauPlein)}
          />
          <Indicateur
            titre="Poids moyen d’un tableau"
            valeur={`${Math.round(chiffres.poidsMoyenDunTableauOctets / (1024 * 1024))} Mo`}
          />
          <Indicateur
            titre="Personnes au plafond de volume"
            valeur={String(chiffres.personnesAuPlafondDeVolume)}
          />
        </div>
      )}
    </main>
  );
}

function Indicateur({ titre, valeur }: { readonly titre: string; readonly valeur: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-muted-foreground text-sm font-normal">{titre}</CardTitle>
      </CardHeader>
      <CardContent>
        <span className="text-2xl font-semibold">{valeur}</span>
      </CardContent>
    </Card>
  );
}
